import { useVistassistantMail } from "@/contexts/VistassistantMailContext";
import type { AssistantQuestion } from "@/domain/entities/vistassistant/AIResponse";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";

interface QuestionCardProps {
    question: AssistantQuestion;
    /** The text right above already asks the question: the card only shows its choices. */
    hideQuestion?: boolean;
    /** A later message exists: the question is past, its choices no longer answer anything. */
    isPast: boolean;
    chosenReply?: string;
}

/**
 * A question the assistant asks instead of guessing (step-based contract), after its answer: a few
 * choices, the most likely first. A click answers the question, nothing more: the choice is sent as
 * the user's next message, as if typed. The user may also ignore the card and write anything else.
 */
const QuestionCard = ({
    question,
    hideQuestion,
    isPast,
    chosenReply,
}: QuestionCardProps) => {
    const { t } = useTranslation();
    const { onAnswerQuestion } = useVistassistantMail();
    const disabled = isPast || !onAnswerQuestion;

    return (
        <div className="flex w-full flex-col gap-3">
            {!hideQuestion && (
                <p className="text-base text-primary-darkest">
                    {question.question}
                </p>
            )}
            <ul className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3">
                {question.choices.map((choice) => {
                    const isChosen = isPast && choice.reply === chosenReply;

                    return (
                        <li key={`${choice.label}\n${choice.detail ?? ""}`}>
                            <button
                                className={cn(
                                    "flex h-full w-full flex-col items-start gap-1 rounded-2xl border bg-white px-4 py-3.5 text-left text-sm transition-colors disabled:cursor-default",
                                    isChosen
                                        ? "border-2 border-primary-dark px-[15px] py-[13px]"
                                        : "border-assistant-cardBorder",
                                    !disabled &&
                                        "cursor-pointer hover:border-primary-dark",
                                )}
                                disabled={disabled}
                                onClick={() => onAnswerQuestion?.(choice.reply)}
                                type="button"
                            >
                                <span className="flex w-full items-start gap-2">
                                    <span className="min-w-0 flex-1 font-semibold text-primary-darkest">
                                        {choice.label}
                                    </span>
                                    {isChosen && (
                                        <span className="flex flex-none items-center gap-1 text-[13px] font-semibold text-primary-dark">
                                            <Check
                                                aria-hidden
                                                className="size-4"
                                                strokeWidth={1.75}
                                            />
                                            {t("vistassistant.question.chosen")}
                                        </span>
                                    )}
                                </span>
                                {choice.detail && (
                                    <span className="text-assistant-muted [overflow-wrap:anywhere]">
                                        {choice.detail}
                                    </span>
                                )}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default QuestionCard;
